import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, requestYieldCallback, mediaQueryLarge } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that renders a paginated product collection with load more button.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [grid] - The grid element.
 * @property {HTMLElement[]} [cards] - The cards elements.
 * @property {HTMLButtonElement} [loadMoreButton] - The load more button.
 * @property {HTMLElement} [progressText] - The progress text element.
 * @property {HTMLElement} [progressBar] - The progress bar fill element.
 * @property {HTMLElement} [pagination] - The pagination wrapper.
 *
 * @extends Component<Refs>
 */
export default class ProductCollection extends Component {
  /**
   * Cached section HTML keyed by the active collection state and page number.
   * @type {Map<string, string>}
   */
  pages = new Map();

  /** @type {boolean} */
  isLoading = false;

  /**
   * Invalidates pagination work started for an older filter or sort state.
   * @type {number}
   */
  #contentGeneration = 0;

  /** @type {boolean} */
  #pendingSectionUpdate = false;

  /** @type {ResizeObserver | undefined} */
  #gridResizeObserver;

  /** @type {number} */
  #lastGridColumnCount = 0;

  /** @type {number} */
  #historyUpdateToken = 0;

  /**
   * Identifies the only load-more operation allowed to update loading UI.
   * Filter and section updates invalidate older operations while their
   * network requests are still settling.
   * @type {number}
   */
  #loadOperationToken = 0;

  /** @type {number} */
  #recoveryMaskToken = 0;

  /**
   * Cached total item count for the active filter set. Read once from
   * grid.dataset.totalItems and reused across pagination updates so
   * #updateProgress doesn't re-parse the attribute on every Show More.
   * Reset by #handleFilterUpdate when filters change.
   * @type {number | undefined}
   */
  #cachedTotalItems;

  connectedCallback() {
    super.connectedCallback();

    // Listen for filter/sort updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    document.addEventListener(ThemeEvents.FilterUpdateError, this.#handleFilterUpdateError);

    // Reset loading state when restored from bfcache
    window.addEventListener('pageshow', this.#handlePageShow);

    // If landing on page > 1 (browser back), hide content during recovery
    // to prevent visible scroll jump, then reveal after positioning.
    const needsRecovery = this.#needsPageRecovery();

    this.#observeGridColumns();
    this.#initializeGrid(needsRecovery);
  }

  /**
   * Refresh refs after a Section Rendering API morph and prepare the newly
   * rendered grid even when filtering keeps the same number of pages.
   */
  updatedCallback() {
    super.updatedCallback();

    // A completed morph is a new DOM epoch even when FilterUpdate already
    // invalidated the request. This also cancels work started in the brief
    // interval between the event and the morph.
    this.#contentGeneration += 1;
    this.#loadOperationToken += 1;
    this.#pendingSectionUpdate = false;
    this.#historyUpdateToken += 1;
    this.pages.clear();
    this.#cachedTotalItems = undefined;
    this.#observeGridColumns();

    const currentPage = Number(new URL(window.location.href).searchParams.get('page'));
    if (currentPage > 1) {
      // A popstate render of ?page=N contains only Shopify's page N. Rebuild
      // pages 1..N just as we do for a direct entry before exposing controls.
      this.#initializeGrid(true);
      return;
    }

    this.#clearGridRecoveryMask();
    this.#prepareGridPage().then(() => {
      if (!this.isConnected) return;
      this.#updateProgress();
      this.#checkHideLoadMore();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    document.removeEventListener(ThemeEvents.FilterUpdateError, this.#handleFilterUpdateError);
    window.removeEventListener('pageshow', this.#handlePageShow);
    this.#gridResizeObserver?.disconnect();
  }

  /**
   * Restore a cached or directly paginated collection without ever leaving
   * the grid permanently hidden when a recovery request fails.
   * @param {boolean} needsRecovery
   */
  async #initializeGrid(needsRecovery) {
    const recoveryMaskToken = needsRecovery ? ++this.#recoveryMaskToken : 0;
    const { grid } = this.refs;
    const initializationGeneration = this.#contentGeneration;
    const initializationStateKey = this.#getStateKey();
    const isCurrent = () =>
      initializationGeneration === this.#contentGeneration &&
      initializationStateKey === this.#getStateKey() &&
      grid === this.refs.grid &&
      this.isConnected &&
      !this.#pendingSectionUpdate;

    try {
      const recoveredFromCache = await this.#recoverPreviousPages();
      if (!isCurrent()) return;

      if (!recoveredFromCache) {
        await this.#loadPreviousPages();
        if (!isCurrent()) return;

        // If we had metadata-only cache (no HTML), restore scroll after API fetch
        if (this._pendingScrollCache) {
          await this.#restoreScroll(this._pendingScrollCache);
          if (!isCurrent()) return;
          this._pendingScrollCache = null;
        }
      }

      await this.#prepareGridPage();
      if (!isCurrent()) return;

      this.#updateProgress();
      this.#checkHideLoadMore();
    } catch (error) {
      console.error('Unable to restore the collection grid.', error);
    } finally {
      if (needsRecovery && recoveryMaskToken === this.#recoveryMaskToken) {
        document.getElementById('collection-restore-hide')?.remove();
        if (history.scrollRestoration) history.scrollRestoration = 'auto';
      }
    }
  }

  /**
   * Recalculate only when the responsive grid actually changes its number of
   * columns. Height changes from image loading do not trigger extra work.
   */
  #observeGridColumns() {
    const { grid } = this.refs;
    if (typeof ResizeObserver !== 'function') return;

    if (!grid) {
      this.#gridResizeObserver?.disconnect();
      this.#lastGridColumnCount = 0;
      return;
    }

    this.#gridResizeObserver ??= new ResizeObserver(() => {
      if (!this.isConnected || this.#pendingSectionUpdate) return;

      const currentGrid = this.refs.grid;
      if (!currentGrid) return;

      const columnCount = this.#getGridColumnCount(currentGrid);
      if (columnCount === this.#lastGridColumnCount) return;
      this.#lastGridColumnCount = columnCount;

      this.#prepareGridPage().then(() => {
        if (!this.isConnected) return;
        this.#updateProgress();
        this.#checkHideLoadMore();
      });
    });

    this.#gridResizeObserver.disconnect();
    this.#lastGridColumnCount = this.#getGridColumnCount(grid);
    this.#gridResizeObserver.observe(grid);
  }

  /**
   * @param {Element} grid
   * @returns {number}
   */
  #getGridColumnCount(grid) {
    return getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length;
  }

  /**
   * Remove grid items temporarily borrowed from the next unloaded page before
   * recalculating alignment for a different responsive column count.
   * @param {Element} grid
   */
  #removeBorrowedItems(grid) {
    for (const item of grid.querySelectorAll('[data-borrowed-grid-item]')) {
      item.remove();
    }
  }

  /**
   * Toggles between mobile layouts (single column and default two columns).
   */
  toggleMobileLayout() {
    const { grid } = this.refs;
    if (!grid) return;

    const currentView = grid.getAttribute('product-grid-view') || 'default';
    const newView = currentView === 'mobile-single' ? 'default' : 'mobile-single';

    this.#animateLayoutChange(newView);
  }

  /**
   * Animates the layout change with view transition.
   *
   * @param {string} value
   */
  #animateLayoutChange = (value) => {
    const { grid } = this.refs;
    if (!grid) return;

    // This mobile control must react synchronously. The shared product-grid
    // view-transition preparation waits for an idle callback and can stall on
    // large, image-heavy collections after navigation or cache restoration.
    grid.setAttribute('product-grid-view', value);

    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    requestIdleCallback(() => {
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Check if current page needs recovery of previous pages
   * @returns {boolean}
   */
  #needsPageRecovery() {
    const currentPage = Number(new URL(window.location.href).searchParams.get('page'));
    return sessionStorage.getItem('product-collection-cache') !== null || currentPage > 1;
  }

  /**
   * Handle filter/sort updates by clearing cached pages and re-fetching
   */
  #handleFilterUpdate = (event) => {
    this.#contentGeneration += 1;
    this.#historyUpdateToken += 1;
    this.#loadOperationToken += 1;
    this.#pendingSectionUpdate = true;
    this.pages.clear();
    this.#cachedTotalItems = undefined;
    this.isLoading = false;

    const { loadMoreButton } = this.refs;
    if (loadMoreButton) {
      loadMoreButton.removeAttribute('aria-busy');
      loadMoreButton.disabled = false;
    }

    const targetPage = Number(event?.detail?.queryParams?.get('page'));
    if (targetPage > 1) this.#hideGridForRecovery();
    else this.#clearGridRecoveryMask();
  };

  /**
   * Restore the existing collection after a failed filter section request.
   * The facets component already rolled the URL and inputs back before this
   * event is dispatched.
   */
  #handleFilterUpdateError = () => {
    this.#contentGeneration += 1;
    this.#historyUpdateToken += 1;
    this.#loadOperationToken += 1;
    this.#pendingSectionUpdate = false;
    this.pages.clear();
    this.#cachedTotalItems = undefined;
    this.isLoading = false;

    this.#clearGridRecoveryMask();

    const { loadMoreButton } = this.refs;
    if (loadMoreButton) {
      loadMoreButton.removeAttribute('aria-busy');
      loadMoreButton.disabled = false;
    }

    this.#prepareGridPage().then(() => {
      if (!this.isConnected) return;
      this.#updateProgress();
      this.#checkHideLoadMore();
    });
  };

  /** Hide an incomplete ?page=N morph until pages 1..N are reconstructed. */
  #hideGridForRecovery() {
    this.#recoveryMaskToken += 1;

    let style = document.getElementById('collection-restore-hide');
    if (!style) {
      style = document.createElement('style');
      style.id = 'collection-restore-hide';
      style.textContent = 'product-collection [ref="grid"]{visibility:hidden;min-height:80vh}';
      document.head.appendChild(style);
    }
    style.dataset.recoveryMask = String(this.#recoveryMaskToken);

    if (history.scrollRestoration) history.scrollRestoration = 'manual';
  }

  /** Invalidate every older recovery and reveal the current complete grid. */
  #clearGridRecoveryMask() {
    this.#recoveryMaskToken += 1;
    document.getElementById('collection-restore-hide')?.remove();
    if (history.scrollRestoration) history.scrollRestoration = 'auto';
  }

  /**
   * Reset loading state when page is restored from bfcache
   */
  #handlePageShow = (event) => {
    if (event.persisted) {
      // bfcache restored the page perfectly — discard stale manual cache
      sessionStorage.removeItem('product-collection-cache');

      this.#contentGeneration += 1;
      this.#historyUpdateToken += 1;
      this.#loadOperationToken += 1;
      this.#pendingSectionUpdate = false;
      this.isLoading = false;
      this.#clearGridRecoveryMask();

      const { loadMoreButton } = this.refs;
      if (loadMoreButton) {
        loadMoreButton.removeAttribute('aria-busy');
        loadMoreButton.disabled = false;
      }
    }
  };

  /**
   * Recover missing pages when landing on page > 1 (e.g., after browser back).
   * Fetches pages 1 through (currentPage - 1), prepends their items, and
   * resets the URL to remove the page param.
   * @returns {Promise<boolean>} whether cache was used
   */
  async #recoverPreviousPages() {
    const { grid } = this.refs;
    if (!grid) return false;

    const recoveryGeneration = this.#contentGeneration;
    const recoveryStateKey = this.#getStateKey();

    const cacheRaw = sessionStorage.getItem('product-collection-cache');
    sessionStorage.removeItem('product-collection-cache');

    if (!cacheRaw) return false;

    let cache;
    try {
      cache = JSON.parse(cacheRaw);
    } catch (e) {
      return false;
    }

    // Verify the cache matches the current URL (ignore page param).
    // If the user navigated away and came back without filters, discard stale cache.
    if (cache.url) {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete('page');
      currentUrl.hash = '';
      const currentKey = currentUrl.pathname + currentUrl.search;

      if (cache.url !== currentKey) return false;
    }

    // Metadata-only cache (HTML was too large for sessionStorage) —
    // fall back to Section Rendering API fetch, but preserve scroll metadata
    if (!cache.html) {
      this._pendingScrollCache = cache;
      return false;
    }

    // Restore grid content from cache (own server-rendered Shopify HTML, safe)
    // eslint-disable-next-line no-unsanitized/property -- trusted Shopify-rendered HTML from own sessionStorage
    grid.innerHTML = cache.html;

    // Restore grid data attributes
    if (cache.totalItems) grid.dataset.totalItems = cache.totalItems;
    if (cache.lastPage) grid.dataset.lastPage = cache.lastPage;

    // Update progress and load more visibility
    this.#updateProgress();
    this.#checkHideLoadMore();

    await this.#restoreScroll(cache);

    if (
      recoveryGeneration !== this.#contentGeneration ||
      recoveryStateKey !== this.#getStateKey() ||
      grid !== this.refs.grid ||
      !this.isConnected ||
      this.#pendingSectionUpdate
    ) {
      return false;
    }

    // Clean page param from URL if present
    const url = new URL(window.location.href);
    if (url.searchParams.has('page')) {
      url.searchParams.delete('page');
      history.replaceState('', '', url.toString());
    }

    return true;
  }

  /**
   * Load previous pages when landing directly on ?page=N (N > 1) without cache,
   * or when metadata-only cache provides pagesLoaded count.
   * Fetches missing pages and appends their items to the grid.
   */
  async #loadPreviousPages() {
    const { grid } = this.refs;
    if (!grid) return;

    // Determine target page: from URL param or metadata-only cache
    const url = new URL(window.location.href);
    const urlPage = Number(url.searchParams.get('page'));
    const cachePage = this._pendingScrollCache?.pagesLoaded || 0;
    const requestedTargetPage = Math.max(urlPage, cachePage);
    const lastPage = Number(grid.dataset.lastPage) || requestedTargetPage;
    const targetPage = Math.min(requestedTargetPage, lastPage);

    if (!targetPage || targetPage <= 1) return;

    const recoveryGeneration = this.#contentGeneration;
    const recoveryStateKey = this.#getStateKey();
    const existingItems = Array.from(grid.querySelectorAll(':scope > .product-grid__item'));
    const loadedPages = new Set(existingItems.map((item) => Number(item.dataset.page)));

    // Shopify renders the page requested by ?page=N. Fetch every missing page
    // from 1 through the recovery target, then rebuild the grid in page order.
    const pagePromises = [];
    for (let page = 1; page <= targetPage; page++) {
      if (loadedPages.has(page)) continue;

      const pageUrl = new URL(window.location.href);
      pageUrl.searchParams.delete('page');
      pageUrl.searchParams.set('page', String(page));
      pagePromises.push(
        sectionRenderer.getSectionHTML(this.sectionId, true, pageUrl).then((html) => ({ page, html }))
      );
    }

    const results = await Promise.all(pagePromises);

    if (
      recoveryGeneration !== this.#contentGeneration ||
      recoveryStateKey !== this.#getStateKey() ||
      grid !== this.refs.grid ||
      !this.isConnected ||
      this.#pendingSectionUpdate
    ) {
      return;
    }

    const itemsByPage = new Map();
    for (const item of existingItems) {
      const page = Number(item.dataset.page);
      const pageItems = itemsByPage.get(page) ?? [];
      pageItems.push(item);
      itemsByPage.set(page, pageItems);
    }

    for (const { page, html } of results) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const gridElement = parsed.querySelector('[ref="grid"]');
      if (!gridElement) continue;

      itemsByPage.set(page, Array.from(gridElement.querySelectorAll(':scope > .product-grid__item')));
    }

    const orderedItems = [];
    for (let page = 1; page <= targetPage; page++) {
      orderedItems.push(...(itemsByPage.get(page) ?? []));
    }

    grid.replaceChildren(...orderedItems);

    this.#updateProgress();
    this.#checkHideLoadMore();
  }

  /**
   * Handle load more button click
   */
  handleLoadMore = async () => {
    if (this.isLoading || this.#pendingSectionUpdate) return;

    const loadOperationToken = ++this.#loadOperationToken;
    this.isLoading = true;
    const { loadMoreButton } = this.refs;

    if (loadMoreButton) {
      loadMoreButton.setAttribute('aria-busy', 'true');
      loadMoreButton.disabled = true;
    }

    try {
      await this.#renderNextPage();
    } catch (error) {
      console.error('Unable to load more collection products.', error);
    } finally {
      if (loadOperationToken !== this.#loadOperationToken) return;

      this.isLoading = false;

      const currentButton = this.refs.loadMoreButton;
      for (const button of new Set([loadMoreButton, currentButton])) {
        if (!button) continue;
        button.removeAttribute('aria-busy');
        button.disabled = false;
      }
    }
  };

  /**
   * Get next page info
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getNextPage() {
    const { grid } = this.refs;
    if (!grid) return;

    // Query DOM directly instead of relying on refs — after innerHTML cache
    // restore, the ref array may not yet be updated by MutationObserver.
    const allCards = grid.querySelectorAll('[data-page]');
    if (!allCards.length) return;

    // Find the highest page number among all cards
    let currentPage = 0;
    for (const card of allCards) {
      const p = Number(card.dataset.page);
      if (p > currentPage) currentPage = p;
    }

    const lastPage = Number(grid.dataset.lastPage);
    const nextPage = currentPage + 1;

    if (nextPage > lastPage) return;

    const url = new URL(window.location.href);
    url.searchParams.set('page', nextPage.toString());
    url.hash = '';

    return { page: nextPage, url };
  }

  /**
   * Fetch next page content
   */
  async #fetchNextPage() {
    const nextPage = this.#getNextPage();
    if (!nextPage) return;

    return this.#fetchPage(nextPage.page, nextPage.url);
  }

  /**
   * Fetch a page only for the collection state that requested it. Responses
   * from older filter/sort states are discarded after the await boundary.
   * @param {number} page
   * @param {URL} [url]
   * @returns {Promise<boolean>}
   */
  async #fetchPage(page, url = undefined) {
    if (this.#pendingSectionUpdate) return false;

    const { grid } = this.refs;
    const lastPage = Number(grid?.dataset.lastPage);
    if (!grid || page < 1 || page > lastPage) return false;

    const pageUrl = url ? new URL(url) : new URL(window.location.href);
    pageUrl.searchParams.set('page', String(page));
    pageUrl.hash = '';

    const fetchGeneration = this.#contentGeneration;
    const fetchStateKey = this.#getStateKey(pageUrl);
    const cacheKey = this.#getPageCacheKey(page, pageUrl);
    if (this.pages.has(cacheKey)) return true;

    try {
      const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageUrl);

      if (
        fetchGeneration !== this.#contentGeneration ||
        fetchStateKey !== this.#getStateKey() ||
        !this.isConnected
      ) {
        return false;
      }

      this.pages.set(cacheKey, pageContent);
      return true;
    } catch (error) {
      if (fetchGeneration === this.#contentGeneration && fetchStateKey === this.#getStateKey()) {
        console.warn(`Unable to fetch collection page ${page}.`, error);
      }
      return false;
    }
  }

  /**
   * @param {URL} [url]
   * @returns {string}
   */
  #getStateKey(url = new URL(window.location.href)) {
    const stateUrl = new URL(url);
    stateUrl.searchParams.delete('page');
    stateUrl.searchParams.delete('section_id');
    stateUrl.searchParams.sort();
    stateUrl.hash = '';
    return `${stateUrl.pathname}${stateUrl.search}`;
  }

  /**
   * @param {number} page
   * @param {URL} [url]
   * @returns {string}
   */
  #getPageCacheKey(page, url = new URL(window.location.href)) {
    return `${this.#getStateKey(url)}::${page}`;
  }

  /**
   * Keep every multi-column grid row complete without changing Shopify's
   * product pagination. An ordered prefix is borrowed from the next page,
   * including any campaign banner, then replaced by that full source page on
   * Show More so global banner order and product uniqueness are preserved.
   */
  async #prepareGridPage() {
    if (this.#pendingSectionUpdate) return;

    const { grid } = this.refs;
    if (!grid) return;

    this.#removeBorrowedItems(grid);
    const gridColumns = this.#getGridColumnCount(grid);

    // A single-column mobile grid is complete by definition. Two-column
    // mobile, tablet and desktop layouts all borrow enough products to avoid
    // leaving a visibly broken final row before Show More.
    if (gridColumns < 2) return;

    const prepareGeneration = this.#contentGeneration;
    const prepareStateKey = this.#getStateKey();
    await this.#fetchNextPage();

    if (
      prepareGeneration !== this.#contentGeneration ||
      prepareStateKey !== this.#getStateKey() ||
      grid !== this.refs.grid ||
      !this.isConnected ||
      this.#pendingSectionUpdate ||
      this.#getGridColumnCount(grid) !== gridColumns
    ) {
      return;
    }

    const nextPage = this.#getNextPage();
    if (!nextPage) return;

    // Keep one additional page warm so Show More does not wait on both the
    // requested page and the following alignment page.
    requestIdleCallback(() => {
      if (
        prepareGeneration !== this.#contentGeneration ||
        prepareStateKey !== this.#getStateKey() ||
        grid !== this.refs.grid ||
        !this.isConnected ||
        this.#pendingSectionUpdate
      ) {
        return;
      }

      this.#fetchPage(nextPage.page + 1);
    });

    const currentItems = grid.querySelectorAll(':scope > .product-grid__item').length;
    const missingItems = (gridColumns - (currentItems % gridColumns)) % gridColumns;
    if (missingItems === 0) return;

    const pageItems = this.#getGridItemsForPage(nextPage.page);
    if (!pageItems) return;

    const borrowedItems = pageItems.slice(0, missingItems);
    if (borrowedItems.length !== missingItems) return;

    for (const item of borrowedItems) {
      // Keep Show More pointing at the page the product came from. The
      // ordered prefix is removed before that full source page is appended.
      item.dataset.page = String(nextPage.page - 1);
      item.dataset.borrowedGridItem = '';
      item.dataset.borrowedFromPage = String(nextPage.page);
    }

    grid.append(...borrowedItems);
  }

  /**
   * Render next page items
   */
  async #renderNextPage() {
    if (this.#pendingSectionUpdate) return;

    const { grid } = this.refs;
    if (!grid) return;

    const renderGeneration = this.#contentGeneration;
    const renderStateKey = this.#getStateKey();
    const nextPage = this.#getNextPage();
    if (!nextPage) return;

    let nextPageItems = this.#getGridItemsForPage(nextPage.page);

    if (!nextPageItems) {
      await this.#fetchNextPage();

      if (
        renderGeneration !== this.#contentGeneration ||
        renderStateKey !== this.#getStateKey() ||
        grid !== this.refs.grid ||
        !this.isConnected
      ) {
        return;
      }

      nextPageItems = this.#getGridItemsForPage(nextPage.page);
      if (!nextPageItems) return;
    }

    // Replace the ordered prefix borrowed for row alignment with the complete
    // source page. Removing and re-appending the prefix preserves its visual
    // position while also keeping banners in their configured global order.
    for (const item of grid.querySelectorAll(
      `[data-borrowed-from-page="${CSS.escape(String(nextPage.page))}"]`
    )) {
      item.remove();
    }
    grid.append(...nextPageItems);

    // Pre-fetch the following page and borrow only the ordered prefix required
    // to keep the final multi-column row complete.
    await this.#prepareGridPage();

    if (
      renderGeneration !== this.#contentGeneration ||
      renderStateKey !== this.#getStateKey() ||
      grid !== this.refs.grid ||
      !this.isConnected
    ) {
      return;
    }

    // Update progress
    this.#updateProgress();

    // history.replaceState isn't user-visible and doesn't need to block the
    // interaction. Defer it past the next paint so the URL update doesn't
    // share a task with the DOM mutations that just landed.
    const nextUrl = nextPage.url.toString();
    const historyToken = ++this.#historyUpdateToken;
    requestYieldCallback(() => {
      if (
        historyToken !== this.#historyUpdateToken ||
        renderGeneration !== this.#contentGeneration ||
        renderStateKey !== this.#getStateKey() ||
        this.#pendingSectionUpdate ||
        !this.isConnected
      ) {
        return;
      }

      history.replaceState('', '', nextUrl);
    });

    // Check if we need to hide the button
    this.#checkHideLoadMore();
  }

  /**
   * Get grid items from cached page HTML
   * @param {number} page
   * @returns {Element[] | undefined}
   */
  #getGridItemsForPage(page) {
    const pageHTML = this.pages.get(this.#getPageCacheKey(page));
    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;

    return Array.from(gridElement.querySelectorAll(':scope > .product-grid__item'));
  }

  /**
   * Update progress indicator
   */
  #updateProgress() {
    const { progressText, progressBar, grid, pagination } = this.refs;

    if (!grid) return;

    // totalItems doesn't change between pagination clicks for a given filter set.
    // Cache the parsed value to avoid re-reading dataset on every Show More.
    this.#cachedTotalItems ??= Number(grid.dataset.totalItems) || 0;
    const totalItems = this.#cachedTotalItems;

    // Count actual visible items
    const visibleItems = grid.querySelectorAll('[ref="cards[]"]').length;

    if (progressText && pagination) {
      const template = pagination.dataset.progressTemplate || 'Viděli jste [count] z [total] produktů';
      progressText.textContent = template
        .replace('[count]', String(visibleItems))
        .replace('[total]', String(totalItems));
    }

    if (progressBar) {
      const percentage = totalItems > 0 ? (visibleItems / totalItems) * 100 : 0;
      progressBar.style.width = `${percentage}%`;
    }
  }

  /**
   * Check if load more button should be hidden
   */
  #checkHideLoadMore() {
    const { grid, pagination } = this.refs;
    if (!grid || !pagination) return;

    // Product count alone is insufficient: a not-yet-committed source page
    // may still contain a campaign banner after all of its products were
    // borrowed for alignment. Hide only when no logical next page remains.
    const nextPage = this.#getNextPage();
    let finalPageFullyBorrowed = false;

    if (nextPage && nextPage.page === Number(grid.dataset.lastPage)) {
      const pageItems = this.#getGridItemsForPage(nextPage.page);
      const borrowedItemCount = grid.querySelectorAll(
        `[data-borrowed-from-page="${CSS.escape(String(nextPage.page))}"]`
      ).length;
      finalPageFullyBorrowed = Boolean(pageItems?.length && borrowedItemCount === pageItems.length);
    }

    pagination.style.display = !nextPage || finalPageFullyBorrowed ? 'none' : '';
  }

  /**
   * Restore scroll position using scrollY with anchor product ID as backup.
   * Uses double requestAnimationFrame for layout to settle, then verifies
   * the anchor element is in viewport — corrects if layout shifts occurred.
   * @param {{ scrollY?: number, anchorProductId?: string }} cache
   */
  async #restoreScroll(cache) {
    const { grid } = this.refs;

    if (cache.scrollY) {
      await new Promise((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.scrollTo(0, cache.scrollY);

            // Verify scroll via anchor element
            if (cache.anchorProductId && grid) {
              const anchor = grid.querySelector(`[data-product-id="${CSS.escape(cache.anchorProductId)}"]`);
              if (anchor) {
                requestAnimationFrame(() => {
                  const rect = anchor.getBoundingClientRect();
                  // If anchor drifted out of viewport, correct scroll
                  if (rect.top < -100 || rect.bottom > window.innerHeight + 100) {
                    anchor.scrollIntoView({ block: 'center' });
                  }
                  resolve();
                });
                return;
              }
            }
            resolve();
          });
        });
      });
    }

  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }
}

if (!customElements.get('product-collection')) {
  customElements.define('product-collection', ProductCollection);
}
